// src/encounter/referral-practitioner-picker.tsx
// [PROD-1395 / PROD-3336] The OPTIONAL practitioner of a speciality referral, in the referral dialog (`referrals` flag ON
// only). Practitioners come from the speciality's own list (useSpecialityPractitioners: doctors AND allied staff). If that
// read fails, or for the PROD-1395 dietitian target (no speciality id), they come from the org doctors list (useDoctorsList),
// filtered to the speciality (lib/referral-practitioners.ts). Nothing picked = "Any <speciality>". Built on the shared
// SearchPicker, like the consent provider picker. Kept in its own component so nothing is fetched with the flag off.
import { useMemo, useState } from 'react';
import { useAuthStore } from '@/auth/auth-store';
import { SearchPicker } from '@/shared/ui/components/search-picker';
import { useDoctorsList } from '@/settings/services/use-doctors-list';
import { isLegacyDietitian, type ReferralSpeciality } from './lib/referral-specialities';
import { useSpecialityPractitioners } from './services/use-speciality-practitioners';
import { practitionersOfSpeciality, type ReferralPractitioner } from './lib/referral-practitioners';

export interface ReferralPractitionerPickerProps {
  speciality: Pick<ReferralSpeciality, 'id' | 'name'>;
  /** The picked practitioner, or null for any. */
  value: ReferralPractitioner | null;
  onChange: (practitioner: ReferralPractitioner | null) => void;
  disabled?: boolean;
}

export function ReferralPractitionerPicker({ speciality, value, onChange, disabled }: ReferralPractitionerPickerProps) {
  const orgId = useAuthStore((s) => s.user?.organizationId ?? null);
  const byId = !isLegacyDietitian(speciality.id);
  const ofSpeciality = useSpecialityPractitioners(byId ? speciality.id : null);
  // The doctors list is read only as the fallback: the dietitian target, or the speciality read failed.
  const fallback = !byId || ofSpeciality.isError;
  const doctors = useDoctorsList(fallback ? orgId : null, false);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const practitioners = useMemo(
    () => (fallback ? practitionersOfSpeciality(doctors.data ?? [], speciality) : (ofSpeciality.data ?? [])),
    [fallback, doctors.data, ofSpeciality.data, speciality],
  );
  const isLoading = fallback ? doctors.isLoading : ofSpeciality.isLoading;
  const any = `Any ${speciality.name.toLowerCase()}`;

  return (
    <SearchPicker
      value={value?.name || any}
      placeholder={any}
      open={disabled ? false : open}
      search={search}
      onOpenChange={disabled ? () => {} : setOpen}
      onSearchChange={setSearch}
      loading={isLoading}
      options={practitioners.map((p) => ({ id: p.id, label: p.name }))}
      onSelect={(id) => onChange(practitioners.find((p) => p.id === id) ?? null)}
      clearable={!!value}
      onClear={() => onChange(null)}
    />
  );
}
